/**
 * recorder.js
 *
 * Pluggable interface for the worker's video-to-MP3 pipeline. The job loop
 * calls `processJob(args)` and expects:
 *   { success: true,  hetznerUrl, fileSize, durationSec, bitrate }
 *   { success: false, error }
 *
 * Phase 2A ships with a clearly-marked placeholder implementation that
 * always fails — useful for testing the framework (auth, FCM, claim,
 * heartbeat, recent-jobs list) without committing to a download strategy.
 *
 * Phase 2B options (one to be chosen and dropped in here):
 *
 *   A) Cobalt API
 *      Pros: zero infrastructure, mature, free.
 *      Cons: when Cobalt returns a "tunnel" URL, the actual transfer goes
 *            through Cobalt's IP — defeats the phone-as-clean-IP goal for
 *            anti-bot purposes. Works fine if the bottleneck is just the
 *            venue's connection, not YouTube's anti-bot.
 *
 *   B) Custom extractor backend (Firebase Function or Hetzner-hosted)
 *      Pros: full control; we can pick which IP YouTube sees.
 *      Cons: we're back to dealing with PoToken / BotGuard / etc. — same
 *            problem the desktop has, just on a different machine.
 *
 *   C) Capacitor wrapper + WebView audio capture
 *      Pros: phone's own IP does EVERYTHING — talks to YouTube as a real
 *            user, records the rendered audio. Bypasses every anti-bot
 *            measure because the request really is a person watching.
 *      Cons: not a pure PWA anymore; needs a native build per platform.
 *            On iOS, also needs careful entitlement work for background
 *            audio + media projection. Highest ceiling, highest floor.
 *
 *   D) Wrapper around an Android-native lib (NewPipeExtractor / yt-dlp via
 *      Termux) with the PWA just being a UI shell.
 *
 * Recommendation: start with A on the Hetzner endpoint (low effort, get
 * end-to-end working), evaluate, then move to C if anti-bot bites.
 */

export async function processJob(args) {
  const { videoId, title, onProgress } = args;
  if (!videoId) {
    return { success: false, error: 'no videoId' };
  }
  console.log('[recorder] processJob (placeholder) for', videoId, title);

  // Surface a clear, debuggable failure rather than hanging.
  if (typeof onProgress === 'function') {
    onProgress({ percent: 0, message: 'Recorder not yet implemented (Phase 2B).' });
    await new Promise((r) => setTimeout(r, 500));
  }
  return {
    success: false,
    error: 'Phase 2B recorder not implemented — choose A/B/C/D in recorder.js and drop the implementation in.',
  };
}
